import React, {useEffect, useState} from 'react';
import { Button, Input, InputNumber, Space, Checkbox } from "antd";
import Table from "@components/ResponsiveTable";
import {
    DeleteOutlined,
    PlusOutlined,
    HolderOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    ArrowLeftOutlined,
    ArrowRightOutlined,
} from "@ant-design/icons";
import {useSelector} from "react-redux";
import { FKPI_TEMPLATE_KIND_KEY } from "@/utils/form-type-generic-compat";
import {
    DndContext,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {restrictToVerticalAxis} from "@dnd-kit/modifiers";
import Select from "@components/ResponsiveSelect";

const {Option} = Select;

/**
 * 可拖拽排序的表格行（手柄在首列内，避免与输入控件的点击冲突）。
 * 仅包在 <SortableContext> + <DndContext> 内使用。
 */
const SortableBodyRow = React.forwardRef((props, forwardedRef) => {
    const {children, ...restProps} = props;
    const rawKey = restProps["data-row-key"];
    const fallbackIdRef = React.useRef(null);
    if ((rawKey == null || rawKey === "") && fallbackIdRef.current == null) {
        fallbackIdRef.current = `_noid_${Math.random().toString(36).slice(2)}`;
    }
    const sortableId =
        rawKey != null && rawKey !== ""
            ? String(rawKey)
            : fallbackIdRef.current;
    const sortDisabled = rawKey == null || rawKey === "";
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({id: sortableId, disabled: sortDisabled});

    const setRefs = React.useCallback(
        (node) => {
            setNodeRef(node);
            if (typeof forwardedRef === "function") {
                forwardedRef(node);
            } else if (forwardedRef) {
                forwardedRef.current = node;
            }
        },
        [forwardedRef, setNodeRef]
    );

    const style = {
        ...restProps.style,
        transform: CSS.Transform.toString(transform),
        transition,
        ...(isDragging ? {position: "relative", zIndex: 1, opacity: 0.92} : {}),
    };

    /** 首列为专用拖拽占位列，避免 clone 破坏 Label 等受控单元格 */
    const cells = React.Children.toArray(children);
    const [dragCell, ...dataCells] = cells;
    let dragCellEl = dragCell;
    if (!sortDisabled && React.isValidElement(dragCell)) {
        dragCellEl = React.cloneElement(dragCell, {
            children: (
                <span
                    ref={setActivatorNodeRef}
                    {...listeners}
                    style={{
                        cursor: "grab",
                        touchAction: "none",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "rgba(0,0,0,0.45)",
                    }}
                    aria-label="Drag to reorder"
                    role="button"
                    tabIndex={0}
                >
                    <HolderOutlined/>
                </span>
            ),
        });
    }

    return (
        <tr ref={setRefs} style={style} {...restProps} {...attributes}>
            {dragCellEl}
            {dataCells}
        </tr>
    );
});
SortableBodyRow.displayName = "SortableBodyRow";

const COLUMN_TYPES = [
    {value: "text", label: "Text"},
    {value: "number", label: "Number"},
    {value: "select", label: "Select"},
    {value: "boolean", label: "Boolean"},
    {value: "na", label: "N/A"},
    {value: "photo", label: "Photo"},
    {value: "textarea", label: "Textarea"},
    {value: "date", label: "Date"},
    {value: "label", label: "Label"},
];

const cellMapKey = (rowId, colId) => `${String(rowId)}:${String(colId)}`;

/** 用于列头「默认类型」展示：该列各格 cell.type 的多数派 */
const majorityCellTypeForColumn = (col, rowList, cellsMap) => {
    const tallies = {};
    (rowList || []).forEach((row) => {
        const key = cellMapKey(row.id, col.id);
        const ct = String(cellsMap[key]?.type || "text").toLowerCase().trim();
        tallies[ct] = (tallies[ct] || 0) + 1;
    });
    let best = "text";
    let bestN = -1;
    Object.keys(tallies).forEach((k) => {
        const n = tallies[k];
        if (n > bestN) {
            bestN = n;
            best = k || "text";
        }
    });
    return best;
};

const pruneCellsMap = (cells, rows, columns) => {
    const rids = new Set((rows || []).map((r) => String(r.id)));
    const cids = new Set((columns || []).map((c) => String(c.id)));
    const next = {};
    Object.keys(cells || {}).forEach((k) => {
        const idx = k.indexOf(":");
        if (idx < 0) return;
        const rid = k.slice(0, idx);
        const cid = k.slice(idx + 1);
        if (rids.has(rid) && cids.has(cid)) {
            next[k] = cells[k];
        }
    });
    return next;
};

/**
 * 与设施端 buildEffectiveMeasurementCell 一致：cells 缺省时的回退为「列 type（非 text）」再「行 type」。
 * 仅在加载旧模板或 API 仍含行/列测量 type 时调用，用于写入 cells；保存时行/列测量 type 会抹平为 text。
 */
const expandLegacyTypesIntoCells = (rows, columns, cellsIn) => {
    const out = {...(cellsIn || {})};
    (rows || []).forEach((row) => {
        (columns || []).forEach((col) => {
            const colType =
                col?.type != null && String(col.type).trim() !== ""
                    ? String(col.type).toLowerCase().trim()
                    : "text";
            if (colType === "label" || colType === "na") return;
            const key = cellMapKey(row.id, col.id);
            const existing = out[key];
            if (existing && existing.type != null && String(existing.type).trim() !== "") return;

            const rowType =
                row?.type != null && String(row.type).trim() !== ""
                    ? String(row.type).toLowerCase().trim()
                    : "text";
            const eff = colType !== "text" && colType !== "" ? colType : rowType;
            if (!eff || eff === "text") return;

            const colHasOpts = col?.options != null && String(col.options).trim() !== "";
            const rowHasOpts = row?.options != null && String(row.options).trim() !== "";
            let opt = null;
            if (eff === "select") {
                if (colHasOpts) opt = col.options;
                else if (rowHasOpts) opt = row.options;
                else if (col?.description != null && String(col.description).trim() !== "") opt = col.description;
                else if (row?.description != null && String(row.description).trim() !== "") opt = row.description;
            }

            out[key] = {
                type: eff,
                ...(eff === "select" && opt != null && String(opt).trim() !== "" ? {options: opt} : {}),
            };
        });
    });
    return out;
};

/**
 * 模板的存盘形态：测量控件类型由 fields.cells 完整描述；行恒为 type:text；
 * Label / N/A 列的 col.type 分别为 label / na（无 cells）；其余列的 type/options 由 cells 多数派推导。
 */
const canonicalizeFieldsForCellsOnlyStorage = (rows, columns, cellsIn) => {
    let cells = {...(cellsIn || {})};
    const colById = new Map((columns || []).map((c) => [String(c.id), c]));
    Object.keys(cells).forEach((k) => {
        const idx = k.indexOf(":");
        if (idx < 0) return;
        const cid = k.slice(idx + 1);
        const col = colById.get(cid);
        const ctl = col && String(col?.type || "").toLowerCase().trim();
        if (ctl === "label" || ctl === "na") {
            delete cells[k];
        }
    });

    (rows || []).forEach((row) => {
        (columns || []).forEach((col) => {
            const colType =
                col?.type != null && String(col.type).trim() !== ""
                    ? String(col.type).toLowerCase().trim()
                    : "text";
            if (colType === "label" || colType === "na") return;
            const key = cellMapKey(row.id, col.id);
            const entry = cells[key];
            if (!entry || entry.type == null || String(entry.type).trim() === "") {
                cells[key] = {...(entry || {}), type: "text"};
            }
        });
    });

    cells = pruneCellsMap(cells, rows, columns);

    const rowsOut = (rows || []).map((r) => ({
        ...r,
        type: "text",
        options: "",
    }));

    /**
     * 列上 type/options 由 cells 推导（多数格子的类型），供 PM Checklist 等仍读 col.type 的消费端；
     * 设施端 Functional Testing 以 cells 为准，可正确处理同列跨行不同类型。
     */
    const deriveColumnFromCells = (col) => {
        const rawT =
            col?.type != null && String(col.type).trim() !== ""
                ? String(col.type).toLowerCase().trim()
                : "text";
        if (rawT === "label") {
            return {...col, type: "label", options: ""};
        }
        if (rawT === "na") {
            return {...col, type: "na", options: ""};
        }
        const tallies = {};
        (rows || []).forEach((row) => {
            const key = cellMapKey(row.id, col.id);
            const cellT = String(cells[key]?.type || "text").toLowerCase().trim();
            tallies[cellT] = (tallies[cellT] || 0) + 1;
        });
        let bestType = "text";
        let bestN = -1;
        Object.keys(tallies).forEach((t) => {
            const n = tallies[t];
            if (n > bestN) {
                bestN = n;
                bestType = t || "text";
            }
        });
        let optionsOut = "";
        if (bestType === "select") {
            for (const row of rows || []) {
                const key = cellMapKey(row.id, col.id);
                const entry = cells[key];
                const et = String(entry?.type || "").toLowerCase().trim();
                if (
                    et === "select" &&
                    entry?.options != null &&
                    String(entry.options).trim() !== ""
                ) {
                    optionsOut = entry.options;
                    break;
                }
            }
        }
        return {
            ...col,
            type: bestType,
            options: optionsOut,
        };
    };

    const columnsOut = (columns || []).map((c) => deriveColumnFromCells(c));

    return {rows: rowsOut, columns: columnsOut, cells};
};

const applyCellsOnlyTemplatePipeline = (rows, columns, cells) => {
    const expanded = expandLegacyTypesIntoCells(rows, columns, cells);
    return canonicalizeFieldsForCellsOnlyStorage(rows, columns, expanded);
};

const TEMPLATE_COLUMN_WIDTH_MIN = 80;
const TEMPLATE_COLUMN_WIDTH_MAX = 660;
/** 新建列与未填写列宽时的占位；须与设施端 Functional Testing `FT_MEASUREMENT_COLUMN_DEFAULT_WIDTH_PX` 一致 */
const TEMPLATE_COLUMN_WIDTH_DEFAULT_PX = 180;

/** 存库用列宽 px；null / 非法则省略，由消费端用默认宽度 */
const normalizeColumnWidthForStorage = (raw) => {
    if (raw == null || raw === "") return null;
    const n = Math.round(Number(raw));
    if (Number.isNaN(n)) return null;
    return Math.min(TEMPLATE_COLUMN_WIDTH_MAX, Math.max(TEMPLATE_COLUMN_WIDTH_MIN, n));
};

let __functionalFormTemplateIdSeq = 0;
const uniqueId = (prefix) => `${prefix}_${Date.now()}_${++__functionalFormTemplateIdSeq}`;

const newRowDefaults = () => ({
    id: uniqueId("row"),
    label: "",
    type: "text",
    description: "",
    unit: "",
    options: "",
    required: false,
});

const newColumnDefaults = () => ({
    id: uniqueId("col"),
    label: "",
    type: "text",
    description: "",
    options: "",
    required: false,
    width: TEMPLATE_COLUMN_WIDTH_DEFAULT_PX,
});

/** Select 选项：与 PM Checklist 一致，支持数组或逗号分隔字符串（存库可为任一形式） */
const optionsToInputValue = (raw) => {
    if (raw == null || raw === "") return "";
    if (Array.isArray(raw)) {
        return raw.map((x) => String(x).trim()).filter((s) => s.length > 0).join(", ");
    }
    return String(raw);
};

/** 列默认类型为 Select 时：用首个非空 options 作为列头「默认选项」展示；编辑后写入整列全部 select 格。 */
const columnSelectDefaultOptionsValue = (col, rowList, cellsMap) => {
    for (const row of rowList || []) {
        const key = cellMapKey(row.id, col.id);
        const entry = cellsMap[key];
        if (String(entry?.type || "").toLowerCase().trim() !== "select") continue;
        const o = entry?.options;
        if (o != null && String(o).trim() !== "") {
            return optionsToInputValue(o);
        }
    }
    return "";
};

/**
 * 新增行时按已有行的列内多数派预填 cells，与列头「Column default type」一致；
 * select 列复制首个非空 options（存库形态，与 columnSelectDefaultOptionsValue 数据源一致）。
 */
const seedCellsForNewRow = (newRow, columns, priorRows, cellsMap) => {
    const next = {...(cellsMap || {})};
    (columns || []).forEach((col) => {
        const ctl = String(col?.type ?? "text").toLowerCase().trim();
        if (ctl === "label" || ctl === "na") return;
        const maj = majorityCellTypeForColumn(col, priorRows, cellsMap);
        const key = cellMapKey(newRow.id, col.id);
        if (maj === "select") {
            let optRaw = null;
            for (const row of priorRows || []) {
                const k = cellMapKey(row.id, col.id);
                const entry = cellsMap[k];
                if (String(entry?.type || "").toLowerCase().trim() !== "select") continue;
                const o = entry?.options;
                if (o != null && String(o).trim() !== "") {
                    optRaw = o;
                    break;
                }
            }
            next[key] =
                optRaw != null ? {type: maj, options: optRaw} : {type: maj};
        } else if (maj === "text") {
            next[key] = {type: "text"};
        } else {
            next[key] = {type: maj, options: ""};
        }
    });
    return next;
};

/** Normalize API values into 模板存盘形态：测量类型仅在 cells；行 type 恒为 text；列为 text | label。 */
export const normalizeFieldsEditorValue = (inputValue) => {
    if (!inputValue) return { rows: [], columns: [], cells: {} };

    let v = inputValue;
    if (typeof v === "string") {
        try {
            v = JSON.parse(v);
        } catch (error) {
            console.error("Error parsing fields:", error);
            return { rows: [], columns: [], cells: {} };
        }
    }

    const normalizeCellsObject = (raw) => {
        if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
        const out = {};
        Object.keys(raw).forEach((k) => {
            const entry = raw[k];
            if (!entry || typeof entry !== "object") return;
            const type =
                entry.type != null && String(entry.type).trim() !== ""
                    ? String(entry.type).trim().toLowerCase()
                    : "";
            const opt = entry.options;
            if (!type && (opt == null || String(opt).trim() === "")) return;
            out[k] = {
                ...(type ? { type } : {}),
                ...(opt != null && String(opt).trim() !== "" ? { options: opt } : {}),
            };
        });
        return out;
    };

    if (v && typeof v === "object" && !Array.isArray(v)) {
        const preservedKind =
            v[FKPI_TEMPLATE_KIND_KEY] === "generic" ? "generic" : null;
        const mapFieldItem = (item) => {
            if (!item || typeof item !== "object") return item;
            const typ = String(item.type || "text").toLowerCase();
            const hasOptions = item.options != null && String(item.options).trim() !== "";
            const labelFromApi =
                item.label != null && String(item.label).trim() !== ""
                    ? item.label
                    : item.name != null
                        ? String(item.name)
                        : "";
            let next = {
                ...item,
                label: labelFromApi,
                options: hasOptions ? item.options : "",
            };
            if (!hasOptions && typ === "select" && item.description) {
                next = {...next, options: item.description, description: ""};
            }
            return next;
        };
        const rowsRaw = Array.isArray(v.rows) ? v.rows.map(mapFieldItem) : [];
        const colsRaw = Array.isArray(v.columns)
            ? v.columns.map(mapFieldItem).map((c) => {
                  if (!c || typeof c !== "object") return c;
                  const w = normalizeColumnWidthForStorage(c.width);
                  if (w == null) {
                      const {width: _omit, ...rest} = c;
                      return rest;
                  }
                  return {...c, width: w};
              })
            : [];
        const cellsRaw = normalizeCellsObject(v.cells);
        const base = applyCellsOnlyTemplatePipeline(rowsRaw, colsRaw, cellsRaw);
        if (preservedKind) {
            return { ...base, [FKPI_TEMPLATE_KIND_KEY]: preservedKind };
        }
        return base;
    }

    // Legacy flat array conversion
    if (Array.isArray(v) && v.length > 0) {
        const convertedRows = v.map((item) => ({
            id: item.id || String(Date.now() + Math.random()),
            label: item.name || item.label || "",
            type: item.type || "text",
            description: item.description || "",
            options: item.options != null ? item.options : "",
            required: item.required || false,
        }));
        const colTypes = new Set();
        v.forEach((item) => {
            if (item.type) colTypes.add(item.type);
        });
        const convertedColumns = Array.from(colTypes).sort().map((t) => ({
            id: `col_${t}`,
            label: t.charAt(0).toUpperCase() + t.slice(1),
            type: t,
            description: "",
            options: "",
            required: false,
        }));
        return applyCellsOnlyTemplatePipeline(convertedRows, convertedColumns, {});
    }

    return { rows: [], columns: [], cells: {} };
};

const FunctionalFormFieldsEditor = (props) => {

    const { value, onChange, formRef, readOnly = false } = props;
    const isPC = useSelector((s) => s.App?.isPC ?? true);
    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 8}})
    );
    /** Form.Item 会注入 onChange；此时必须由父级 value 作为单一数据源，否则 useEffect 与本地 setState 会与表单 store 竞态，type 等字段会被旧 value 拉回默认值 text。 */
    const formControlled = typeof onChange === "function";
    const [legacyRows, setLegacyRows] = useState([]);
    const [legacyColumns, setLegacyColumns] = useState([]);
    const [legacyCells, setLegacyCells] = useState({});

    useEffect(() => {
        if (formControlled) return;
        const parsed = normalizeFieldsEditorValue(value);
        setLegacyRows(parsed.rows);
        setLegacyColumns(parsed.columns);
        setLegacyCells(parsed.cells || {});
    }, [value, formControlled]);

    const parsed = normalizeFieldsEditorValue(value);
    const rows = formControlled ? parsed.rows : legacyRows;
    const columns = formControlled ? parsed.columns : legacyColumns;
    const cells = formControlled ? (parsed.cells || {}) : legacyCells;

    const notifyChange = (newRows, newColumns, newCells) => {
        const cellSource = newCells !== undefined ? newCells : cells;
        const pruned = pruneCellsMap(cellSource, newRows, newColumns);
        const canon = canonicalizeFieldsForCellsOnlyStorage(newRows, newColumns, pruned);
        const next = {rows: canon.rows, columns: canon.columns, cells: canon.cells};
        if (!formControlled) {
            setLegacyRows(canon.rows);
            setLegacyColumns(canon.columns);
            setLegacyCells(canon.cells);
        }
        if (typeof onChange === "function") {
            onChange(next);
        } else if (formRef && typeof formRef.setFieldsValue === "function") {
            formRef.setFieldsValue({fields: next});
        }
    };

    const updateMatrixCell = (rowId, colId, partial) => {
        const key = cellMapKey(rowId, colId);
        const prev = cells[key] || {};
        const merged = {...prev, ...partial};
        if (merged.type == null || String(merged.type).trim() === "") {
            delete merged.type;
        }
        if (merged.options == null || String(merged.options).trim() === "") {
            delete merged.options;
        }
        const nextCells = {...cells};
        if (Object.keys(merged).length === 0) {
            delete nextCells[key];
        } else {
            nextCells[key] = merged;
        }
        notifyChange(rows, columns, nextCells);
    };

    // ── Handlers ────────────────────────────────────────────

    const handleRowChange = (index, key, val) => {
        const newRows = [...rows];
        newRows[index] = {...newRows[index], [key]: val};
        notifyChange(newRows, columns);
    };
    const handleAddRow = (index) => {
        const nr = newRowDefaults();
        const newRows = [...rows];
        newRows.splice(index + 1, 0, nr);
        const nextCells = seedCellsForNewRow(nr, columns, rows, cells);
        notifyChange(newRows, columns, nextCells);
    };
    const handleDeleteRow = (index) => {
        notifyChange(rows.filter((_, i) => i !== index), columns);
    };

    const handleColumnChange = (index, key, val) => {
        const newColumns = [...columns];
        const prev = newColumns[index] || {};
        if (key === "width") {
            const w = normalizeColumnWidthForStorage(val);
            if (w == null) {
                const {width: _omit, ...rest} = prev;
                newColumns[index] = rest;
            } else {
                newColumns[index] = {...prev, width: w};
            }
        } else {
            newColumns[index] = {...prev, [key]: val};
        }
        notifyChange(rows, newColumns);
    };
    const handleAddColumn = (index) => {
        const newColumns = [...columns];
        newColumns.splice(index + 1, 0, newColumnDefaults());
        notifyChange(rows, newColumns);
    };
    const handleDeleteColumn = (index) => {
        notifyChange(rows, columns.filter((_, i) => i !== index));
    };

    const moveRow = (index, delta) => {
        const next = index + delta;
        if (next < 0 || next >= rows.length) return;
        notifyChange(arrayMove(rows, index, next), columns);
    };

    const moveColumn = (index, delta) => {
        const next = index + delta;
        if (next < 0 || next >= columns.length) return;
        notifyChange(rows, arrayMove(columns, index, next));
    };

    const handleRowDragEnd = (event) => {
        const {active, over} = event;
        if (!over || active.id === over.id) return;
        const oldIndex = rows.findIndex((r) => String(r.id) === String(active.id));
        const newIndex = rows.findIndex((r) => String(r.id) === String(over.id));
        if (oldIndex < 0 || newIndex < 0) return;
        notifyChange(arrayMove(rows, oldIndex, newIndex), columns);
    };

    /** 列默认类型：一键写入该列所有数据格；选 Label 则整列为展示列（与存库 col.type=label 一致）。 */
    const applyTypeToWholeColumn = (colIndex, typeStr) => {
        const col = columns[colIndex];
        if (!col) return;
        const t = String(typeStr || "text").toLowerCase().trim();
        const newColumns = columns.map((c, i) => {
            if (i !== colIndex) return c;
            if (t === "label") return {...c, type: "label"};
            if (t === "na") return {...c, type: "na"};
            return {...c, type: "text"};
        });
        const newCells = {...cells};
        if (t === "label" || t === "na") {
            const cid = String(col.id);
            Object.keys(newCells).forEach((k) => {
                const idx = k.indexOf(":");
                if (idx < 0) return;
                if (k.slice(idx + 1) === cid) {
                    delete newCells[k];
                }
            });
        } else {
            rows.forEach((row) => {
                const key = cellMapKey(row.id, col.id);
                if (t === "select") {
                    newCells[key] = {...(newCells[key] || {}), type: t};
                } else {
                    newCells[key] = {...(newCells[key] || {}), type: t, options: ""};
                }
            });
        }
        notifyChange(rows, newColumns, newCells);
    };

    /** 列默认 Select：将默认选项（逗号分隔）写入该列每一格（保持 type 为 select）。 */
    const applySelectDefaultOptionsToWholeColumn = (colIndex, optionsStr) => {
        const col = columns[colIndex];
        if (!col) return;
        const ct0 = String(col?.type ?? "text").toLowerCase().trim();
        if (ct0 === "label" || ct0 === "na") return;
        const newCells = {...cells};
        const val = optionsStr != null ? String(optionsStr) : "";
        rows.forEach((row) => {
            const key = cellMapKey(row.id, col.id);
            const prev = newCells[key] || {};
            newCells[key] = {
                ...prev,
                type: "select",
                options: val,
            };
        });
        notifyChange(rows, columns, newCells);
    };

    const buildMatrixTableColumns = () => {
        const cols = [];
        if (!readOnly && isPC) {
            cols.push({
                title: "",
                key: "_sort",
                width: 40,
                align: "center",
                className: "functional-form-fields-editor-sort-col",
                render: () => null,
            });
        }

        const cornerHeader = readOnly ? (
            <span style={{fontWeight: 600}}>Row</span>
        ) : (
            <div>
                <div style={{fontSize: 12, color: "#8c8c8c", marginBottom: 6}}>Add</div>
                <Space size={4} wrap>
                    <Button
                        type="dashed"
                        size="small"
                        htmlType="button"
                        icon={<PlusOutlined/>}
                        onClick={(e) => {
                            e.preventDefault();
                            const nr = newRowDefaults();
                            notifyChange(
                                [...rows, nr],
                                columns,
                                seedCellsForNewRow(nr, columns, rows, cells)
                            );
                        }}
                    >
                        Row
                    </Button>
                    <Button
                        type="dashed"
                        size="small"
                        htmlType="button"
                        icon={<PlusOutlined/>}
                        onClick={(e) => {
                            e.preventDefault();
                            notifyChange(rows, [...columns, newColumnDefaults()]);
                        }}
                    >
                        Col
                    </Button>
                </Space>
            </div>
        );

        cols.push({
            title: cornerHeader,
            key: "_rowCorner",
            fixed: "left",
            width: !readOnly && isPC ? 216 : 176,
            render: (text, record, rowIndex) => {
                if (readOnly) {
                    return (
                        <div>
                            <div style={{fontWeight: 500}}>{record.label || record.id || "—"}</div>
                            {record.description ? (
                                <div style={{fontSize: 12, color: "#888"}}>{record.description}</div>
                            ) : null}
                            {record.unit ? (
                                <div style={{fontSize: 11, color: "#666"}}>Unit: {record.unit}</div>
                            ) : null}
                            {record.required ? (
                                <div style={{fontSize: 11, color: "#888"}}>Required</div>
                            ) : null}
                        </div>
                    );
                }
                return (
                    <Space direction="vertical" size={6} style={{width: "100%"}}>
                        <Input
                            value={record.label}
                            placeholder="Row label"
                            onChange={(e) => handleRowChange(rowIndex, "label", e.target.value)}
                        />
                        <Input
                            value={record.description}
                            placeholder="Description"
                            onChange={(e) => handleRowChange(rowIndex, "description", e.target.value)}
                        />
                        <Input
                            value={record.unit || ""}
                            placeholder="Unit (e.g. °F, %, CFM)"
                            onChange={(e) => handleRowChange(rowIndex, "unit", e.target.value)}
                        />
                        <Checkbox
                            checked={record.required || false}
                            onChange={(e) => handleRowChange(rowIndex, "required", e.target.checked)}
                        >
                            Required
                        </Checkbox>
                        <Space size={4} wrap>
                            {!isPC ? (
                                <>
                                    <Button
                                        type="default"
                                        htmlType="button"
                                        size="small"
                                        icon={<ArrowUpOutlined/>}
                                        disabled={rowIndex === 0}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            moveRow(rowIndex, -1);
                                        }}
                                    />
                                    <Button
                                        type="default"
                                        htmlType="button"
                                        size="small"
                                        icon={<ArrowDownOutlined/>}
                                        disabled={rowIndex >= rows.length - 1}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            moveRow(rowIndex, 1);
                                        }}
                                    />
                                </>
                            ) : null}
                            <Button
                                type="primary"
                                htmlType="button"
                                size="small"
                                icon={<PlusOutlined/>}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddRow(rowIndex);
                                }}
                            />
                            <Button
                                danger
                                htmlType="button"
                                size="small"
                                icon={<DeleteOutlined/>}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteRow(rowIndex);
                                }}
                            />
                        </Space>
                    </Space>
                );
            },
        });

        columns.forEach((col, colIndex) => {
            const colIsLabel =
                String(col?.type ?? "text").toLowerCase().trim() === "label";
            const colIsNa = String(col?.type ?? "text").toLowerCase().trim() === "na";

            cols.push({
                title: readOnly ? (
                    <div>
                        <div style={{fontWeight: 500}}>{col.label || col.id}</div>
                        <div style={{fontSize: 11, color: "#888"}}>
                            {colIsLabel
                                ? "Label column (no inputs)"
                                : colIsNa
                                  ? "N/A column (disabled in Functional Testing)"
                                  : (() => {
                                      const maj = majorityCellTypeForColumn(col, rows, cells);
                                      const majLbl =
                                          COLUMN_TYPES.find((x) => x.value === maj)?.label ||
                                          "Text";
                                      const optHint =
                                          maj === "select"
                                              ? columnSelectDefaultOptionsValue(col, rows, cells)
                                              : "";
                                      return `Column default: ${majLbl}${
                                          optHint ? ` — ${optHint}` : ""
                                      }`;
                                  })()}
                        </div>
                    </div>
                ) : (
                    <Space direction="vertical" size={6} style={{width: "100%", minWidth: 168}}>
                        <Input
                            value={col.label}
                            placeholder="Column label"
                            onChange={(e) => handleColumnChange(colIndex, "label", e.target.value)}
                        />
                        <div style={{fontSize: 11, color: "#8c8c8c"}}>
                            Column default type (applies to whole column)
                        </div>
                        <Select
                            value={
                                colIsLabel
                                    ? "label"
                                    : colIsNa
                                      ? "na"
                                      : majorityCellTypeForColumn(col, rows, cells)
                            }
                            style={{width: "100%"}}
                            onChange={(v) =>
                                applyTypeToWholeColumn(
                                    colIndex,
                                    v != null ? String(v).toLowerCase() : "text"
                                )
                            }
                        >
                            {COLUMN_TYPES.map((ct) => (
                                <Option key={ct.value} value={ct.value}>
                                    {ct.label}
                                </Option>
                            ))}
                        </Select>
                        {!colIsLabel &&
                        !colIsNa &&
                        majorityCellTypeForColumn(col, rows, cells) === "select" ? (
                            <>
                                <div style={{fontSize: 11, color: "#8c8c8c"}}>
                                    Default options (comma-separated, whole column)
                                </div>
                                <Input
                                    value={columnSelectDefaultOptionsValue(col, rows, cells)}
                                    placeholder="e.g. Yes, No, N/A"
                                    onChange={(e) =>
                                        applySelectDefaultOptionsToWholeColumn(
                                            colIndex,
                                            e.target.value
                                        )
                                    }
                                />
                            </>
                        ) : null}
                        <Input
                            value={col.description}
                            placeholder="Description"
                            onChange={(e) =>
                                handleColumnChange(colIndex, "description", e.target.value)
                            }
                        />
                        <div style={{fontSize: 11, color: "#8c8c8c"}}>
                            Column width (px), clear = use {TEMPLATE_COLUMN_WIDTH_DEFAULT_PX}
                        </div>
                        <InputNumber
                            style={{width: "100%"}}
                            min={TEMPLATE_COLUMN_WIDTH_MIN}
                            max={TEMPLATE_COLUMN_WIDTH_MAX}
                            allowClear
                            placeholder={String(TEMPLATE_COLUMN_WIDTH_DEFAULT_PX)}
                            value={col.width != null && col.width !== "" ? Number(col.width) : undefined}
                            onChange={(v) => handleColumnChange(colIndex, "width", v)}
                        />
                        <Checkbox
                            checked={col.required || false}
                            onChange={(e) =>
                                handleColumnChange(colIndex, "required", e.target.checked)
                            }
                        >
                            Required
                        </Checkbox>
                        <Space size={4} wrap>
                            <Button
                                type="default"
                                htmlType="button"
                                size="small"
                                icon={<ArrowLeftOutlined/>}
                                disabled={colIndex === 0}
                                onClick={(e) => {
                                    e.preventDefault();
                                    moveColumn(colIndex, -1);
                                }}
                            />
                            <Button
                                type="default"
                                htmlType="button"
                                size="small"
                                icon={<ArrowRightOutlined/>}
                                disabled={colIndex >= columns.length - 1}
                                onClick={(e) => {
                                    e.preventDefault();
                                    moveColumn(colIndex, 1);
                                }}
                            />
                            <Button
                                type="primary"
                                htmlType="button"
                                size="small"
                                icon={<PlusOutlined/>}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleAddColumn(colIndex);
                                }}
                            />
                            <Button
                                danger
                                htmlType="button"
                                size="small"
                                icon={<DeleteOutlined/>}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleDeleteColumn(colIndex);
                                }}
                            />
                        </Space>
                    </Space>
                ),
                key: String(col.id),
                width:
                    normalizeColumnWidthForStorage(col?.width) ?? TEMPLATE_COLUMN_WIDTH_DEFAULT_PX,
                render: (text, row, rowIndex) => {
                    if (colIsLabel) {
                        return (
                            <span style={{color: "#8c8c8c", fontSize: 12}} title="Label column — no input">
                                —
                            </span>
                        );
                    }
                    if (colIsNa) {
                        return (
                            <span
                                style={{color: "#94a3b8", fontSize: 12, fontWeight: 500}}
                                title="N/A column — not applicable"
                            >
                                N/A
                            </span>
                        );
                    }
                    const ck = cellMapKey(row.id, col.id);
                    const cell = cells[ck] || {};
                    const rawT =
                        cell.type != null ? String(cell.type).trim().toLowerCase() : "";
                    const disp = rawT || "text";
                    if (readOnly) {
                        const optLabel =
                            disp === "select" && cell.options
                                ? optionsToInputValue(cell.options)
                                : "";
                        return (
                            <div>
                                <div>
                                    {COLUMN_TYPES.find((x) => x.value === disp)?.label || disp}
                                </div>
                                {optLabel ? (
                                    <div style={{fontSize: 11, color: "#888"}}>{optLabel}</div>
                                ) : null}
                            </div>
                        );
                    }
                    const showOpts = disp === "select";
                    return (
                        <Space direction="vertical" size={4} style={{width: "100%"}}>
                            <Select
                                style={{width: "100%"}}
                                value={disp}
                                onChange={(v) => {
                                    const nt = v != null ? String(v).toLowerCase() : "text";
                                    updateMatrixCell(row.id, col.id, {
                                        type: nt,
                                        ...(nt !== "select" ? {options: ""} : {}),
                                    });
                                }}
                            >
                                {COLUMN_TYPES.map((ct) => (
                                    <Option key={ct.value} value={ct.value}>
                                        {ct.label}
                                    </Option>
                                ))}
                            </Select>
                            {showOpts ? (
                                <Input
                                    size="small"
                                    value={
                                        cell.options != null
                                            ? optionsToInputValue(cell.options)
                                            : ""
                                    }
                                    placeholder="Options (comma-separated)"
                                    onChange={(e) =>
                                        updateMatrixCell(row.id, col.id, {
                                            options: e.target.value,
                                        })
                                    }
                                />
                            ) : null}
                        </Space>
                    );
                },
            });
        });

        return cols;
    };

    const tableComponents = !readOnly && isPC ? {body: {row: SortableBodyRow}} : undefined;
    const cornerW = !readOnly && isPC ? 216 : 176;
    const scrollPad = !readOnly && isPC ? 40 : 0;
    const dynamicColsW = (columns || []).reduce(
        (sum, col) =>
            sum +
            (normalizeColumnWidthForStorage(col?.width) ?? TEMPLATE_COLUMN_WIDTH_DEFAULT_PX),
        0
    );
    const scrollX =
        scrollPad +
        cornerW +
        Math.max(dynamicColsW, TEMPLATE_COLUMN_WIDTH_DEFAULT_PX);

    const matrixTable = (
        <Table
            columns={buildMatrixTableColumns()}
            dataSource={rows}
            pagination={false}
            rowKey="id"
            size="small"
            scroll={{x: scrollX}}
            components={tableComponents}
            locale={{
                emptyText: readOnly ? (
                    "No rows"
                ) : (
                    <Button
                        type="dashed"
                        htmlType="button"
                        onClick={() => {
                            const nr = newRowDefaults();
                            notifyChange(
                                [...rows, nr],
                                columns,
                                seedCellsForNewRow(nr, columns, rows, cells)
                            );
                        }}
                    >
                        Add row
                    </Button>
                ),
            }}
        />
    );

    if (rows.length === 0 && columns.length === 0) {
        return (
            <div className="functional-form-fields-editor">
                {readOnly ? (
                    <span style={{color: "#8c8c8c"}}>No fields</span>
                ) : (
                    <Space direction="vertical" size={12}>
                        <span style={{fontSize: 13, color: "#666"}}>
                            edit labels and cell types; column &quot;default type&quot; sets every cell in that column at once (option Label = header-only column).
                        </span>
                        <Button
                            type="primary"
                            htmlType="button"
                            onClick={() =>
                                notifyChange([newRowDefaults()], [newColumnDefaults()])
                            }
                        >
                            Add first row and column
                        </Button>
                    </Space>
                )}
            </div>
        );
    }

    return (
        <div className="functional-form-fields-editor">
            <div style={{marginBottom: 10, color: "#8c8c8c", fontSize: 12}}>
                {readOnly
                    ? "Rows, columns, and cell types."
                    : "One grid: column default type applies to the whole column. Edit body cells one-by-one for row-level differences. PC: drag row grip to reorder rows; use arrows in column headers to reorder columns."}
            </div>
            {!readOnly && columns.length === 0 && rows.length > 0 ? (
                <div style={{marginBottom: 10}}>
                    <Button
                        type="dashed"
                        htmlType="button"
                        icon={<PlusOutlined/>}
                        onClick={() => notifyChange(rows, [newColumnDefaults()])}
                    >
                        Add column
                    </Button>
                </div>
            ) : null}
            {!readOnly && isPC && rows.length > 0 ? (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={handleRowDragEnd}
                >
                    <SortableContext
                        items={rows.map((r) => String(r.id))}
                        strategy={verticalListSortingStrategy}
                    >
                        {matrixTable}
                    </SortableContext>
                </DndContext>
            ) : (
                matrixTable
            )}
            {!readOnly && isPC && rows.length > 1 ? (
                <div style={{color: "#8c8c8c", fontSize: 12, marginTop: 8}}>
                    Drag the left grip on a row to reorder rows.
                </div>
            ) : null}
            {!readOnly && !isPC && rows.length > 1 ? (
                <div style={{color: "#8c8c8c", fontSize: 12, marginTop: 8}}>
                    Use the up / down buttons in each row block to reorder.
                </div>
            ) : null}
        </div>
    );
};

export default FunctionalFormFieldsEditor;
